import React, { FC, useContext, useState } from 'react'
import { LoaderCircle, PlusIcon } from 'lucide-react'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '../../../../ui/dropdown-menu'
import { IconCreate } from '../../../../icons/IconCreate'
import { Button } from '../../../../ui/button'
import { IconCancel } from '../../../../icons/IconCancel'
import { IconTransaction } from '../../../../icons/IconTransaction'
import { IconBanknotes } from '../../../../icons/IconBanknotes'
import { IconCheckBook } from '../../../../icons/IconCheckBook'
import { IconMagneticCard } from '../../../../icons/IconMagneticCard'
import { IconMuseum } from '../../../../icons/IconMuseum'
import { MarkPaidByCashOptions } from '../MarkPaidByCashOptions/MarkPaidByCashOptions'
import { ACHPayment } from '../ACHPayment/ACHPayment'
import { CartPayment } from '../CartPayment/CartPayment'
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../../ui/tooltip'

import { useAppSelector } from '../../../../../store/hooks'
import { selectProfileData } from '../../../../../store/user/userSlice'

import {
  IPaymentsCards,
  ISelectedCard,
  IUserInterface,
} from '../../../../../models/users/user.interface'

import { MODAL_CONSTS } from '../../../../../const/modal-const'
import { HandleModalContext } from '../../../../../layout/mainlayout/MainLayout'
import { IPaymentType, PAYMENT_TYPE } from '../../../../../types/payment'
import { IEventReviewPublish } from '../../../../../models/event-review-publish/event-review-publish.interface'

interface PaymentAllActionsProps {
  type: 'class' | 'ticket' | 'fee'
  paymentsCards: IPaymentsCards
  organizer: IUserInterface | null
  setPaymentsCards: (cards: IPaymentsCards) => void
  user: IUserInterface | null
  haveScratched: boolean
  isRefundPending: boolean
  haveNotPaid: boolean
  onScratch: () => Promise<void>
  onRefund: () => Promise<void>
  onMarkPaidAction: (values: {
    type: IPaymentType
    checkNumber?: string
    checkMemo?: string
  }) => Promise<void>
  onPayByCard: (card: ISelectedCard) => Promise<void>
  onAddNew: () => void
  eventReviewPublish?: IEventReviewPublish | null
}

export const PaymentAllActions: FC<PaymentAllActionsProps> = ({
  type,
  paymentsCards,
  organizer,
  setPaymentsCards,
  user,
  haveScratched,
  isRefundPending,
  haveNotPaid,
  onScratch,
  onRefund,
  onMarkPaidAction,
  onPayByCard,
  onAddNew,
  eventReviewPublish,
}) => {
  const handleModalContext = useContext(HandleModalContext)

  const profileData = useAppSelector(selectProfileData)

  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)

  const allowScratches =
    eventReviewPublish?.EventDetails?.allowScratches || profileData.id === eventReviewPublish?.owner

  const allowAdds =
    eventReviewPublish?.EventDetails?.allowAdds || profileData.id === eventReviewPublish?.owner

  const allowCardPay =
    eventReviewPublish?.EventPaymentSettings?.allowCreditCardPayment ||
    eventReviewPublish?.EventPaymentSettings?.allowACHPayment ||
    organizer?.id === profileData.id
  const allowACHPay =
    eventReviewPublish?.EventPaymentSettings?.allowACHPayment || organizer?.id === profileData.id

  return (
    <DropdownMenu dir="rtl" open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="link" className="rounded-full p-0 group">
          {loading ? (
            <LoaderCircle className="animate-spin" />
          ) : (
            <IconCreate className="!w-5 !h-auto group-hover:fill-SeabiscuitMainThemeColor" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-[260px]" align="start">
        <DropdownMenuGroup>
          <Tooltip>
            <TooltipTrigger className="w-full">
              <DropdownMenuItem className="w-full" disabled={!allowAdds} onClick={onAddNew}>
                <PlusIcon className="w-[14px]" strokeWidth={3} />
                Add {type}
              </DropdownMenuItem>
            </TooltipTrigger>
            <TooltipContent hidden={allowAdds}>Disabled by show</TooltipContent>
          </Tooltip>
        </DropdownMenuGroup>

        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger
              disabled={!haveNotPaid || !allowCardPay}
              className="dropdown-menu-sub-trigger"
            >
              <IconMagneticCard className="w-[12px]" />
              Charge all to credit card
            </DropdownMenuSubTrigger>
            <CartPayment
              paymentsCards={paymentsCards}
              setPaymentsCards={setPaymentsCards}
              setLoading={setLoading}
              onPayByCardAction={onPayByCard}
              organizer={organizer || null}
              user={user}
            />
          </DropdownMenuSub>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger
              disabled={!haveNotPaid || !allowACHPay}
              className="dropdown-menu-sub-trigger"
            >
              <IconMuseum className="w-[12px]" />
              Pay all via ACH payment
            </DropdownMenuSubTrigger>
            <ACHPayment
              paymentsCards={paymentsCards}
              setPaymentsCards={setPaymentsCards}
              user={user}
              organizer={organizer || null}
              setLoading={setLoading}
              onPayByCardAction={onPayByCard}
            />
          </DropdownMenuSub>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />

        <DropdownMenuGroup>
          {organizer?.id === profileData.id && (
            <DropdownMenuSub>
              <DropdownMenuSubTrigger disabled={!haveNotPaid} className="dropdown-menu-sub-trigger">
                <IconBanknotes className="w-[12px] fill-black" />
                Mark all paid by cash
              </DropdownMenuSubTrigger>
              <MarkPaidByCashOptions setLoading={setLoading} onMarkPaidAction={onMarkPaidAction} />
            </DropdownMenuSub>
          )}
          {organizer?.id === profileData.id && (
            <DropdownMenuItem
              disabled={!haveNotPaid}
              onClick={async () => {
                handleModalContext?.handleModal(true, MODAL_CONSTS.CHECK_DETAILS, {
                  ...handleModalContext?.dataToPassOn,
                })
                handleModalContext?.handleModal(true, MODAL_CONSTS.CHECK_DETAILS, {
                  ...handleModalContext?.dataToPassOn,
                  onSave: async (values: { checkNumber: string; checkMemo: string }) =>
                    await onMarkPaidAction({
                      type: PAYMENT_TYPE.CHECK,
                      checkNumber: values.checkNumber,
                      checkMemo: values.checkMemo,
                    }),
                })
              }}
            >
              <IconCheckBook className="w-[12px] fill-black" />
              Mark all paid by check
            </DropdownMenuItem>
          )}
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuItem
            disabled={!haveScratched || !allowScratches}
            onClick={async () => {
              setLoading(true)
              await onScratch()
              setLoading(false)
            }}
          >
            <IconCancel className="w-[12px]" />
            Scratch all
          </DropdownMenuItem>
          {organizer?.id === profileData.id && (
            <DropdownMenuItem
              disabled={!isRefundPending}
              onClick={async () => {
                setLoading(true)
                await onRefund()
                setLoading(false)
              }}
            >
              <IconTransaction className="w-[12px]" />
              Refund all
            </DropdownMenuItem>
          )}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
